import type { LineSeriesOption } from 'echarts/charts';

import type { CreditHistoryChartConfig, CreditHistoryPoint } from './types';

// amcharts4 `getBulletDyPosition` flips both labels above the bullet for very low scores, where
// there is no room below them before the axis. Same threshold, same direction.
const LOW_SCORE_LABEL_FLIP = 420;

// Rich-text style keys — one per delta sign, since ECharts resolves a rich fragment's color from
// its style name and a single series label cannot carry per-point colors any other way.
const deltaStyleFor = (delta: number): 'deltaPositive' | 'deltaNegative' | 'deltaNeutral' => {
  if (delta > 0) return 'deltaPositive';
  if (delta < 0) return 'deltaNegative';
  return 'deltaNeutral';
};

// amcharts4 renders `+5` for gains and the bare number otherwise (`0`, `-12`).
const formatDelta = (delta: number): string => (delta > 0 ? `+${delta}` : String(delta));

export const buildCreditHistorySeries = (
  data: CreditHistoryPoint[],
  config: CreditHistoryChartConfig,
): LineSeriesOption[] => {
  const { theme, lineColor, bulletHoverColor, scoreLabelColor, deltaColors } = config;

  const points = data.map(({ timestamp, score, delta }) => {
    // Padded month markers carry no bullet, label or tooltip — just the x extent.
    if (score === null) return { value: [timestamp, null] };

    const hoverColor = bulletHoverColor(score);
    return {
      value: [timestamp, score],
      // amcharts4 swaps the bullet's fill *and* stroke to the band color on hover.
      emphasis: { itemStyle: { color: hoverColor, borderColor: hoverColor } },
      label: {
        position: (score < LOW_SCORE_LABEL_FLIP ? 'top' : 'bottom') as 'top' | 'bottom',
        formatter: `{score|${score}}\n{${deltaStyleFor(delta)}|${formatDelta(delta)}}`,
      },
    };
  });

  return [
    {
      type: 'line',
      name: 'History Line Series',
      data: points,
      // amcharts4 LineSeries defaults `connect` to true — the line bridges the padded null months
      // instead of breaking into per-month segments.
      connectNulls: true,
      showSymbol: true,
      symbol: 'circle',
      // amcharts4 `circle.radius = 4`
      symbolSize: 8,
      lineStyle: { color: lineColor, width: 2 },
      itemStyle: { color: lineColor },
      z: 3,
      label: {
        show: true,
        // Two stacked lines in one label: the score, then the signed delta beneath it. A second
        // series would be the alternative, but rich text keeps the pair glued to one bullet.
        rich: {
          score: { fontSize: 13, lineHeight: 18, color: scoreLabelColor, fontFamily: theme.fontFamily },
          deltaPositive: { fontSize: 11, lineHeight: 14, color: deltaColors.positive, fontFamily: theme.fontFamily },
          deltaNegative: { fontSize: 11, lineHeight: 14, color: deltaColors.negative, fontFamily: theme.fontFamily },
          deltaNeutral: { fontSize: 11, lineHeight: 14, color: deltaColors.neutral, fontFamily: theme.fontFamily },
        },
        distance: 6,
      },
      emphasis: { scale: false },
    },
  ];
};
